/**
 * engine/prefetch.ts — the head of the next track, asked for before it is needed.
 *
 * Task 08, carried over from the old project's `prefetch.ts`. `spec.md` «Стриминг и кэш»:
 * «Приложение заранее подгружает первые 512 КБ следующего трека за 20 секунд до конца
 * текущего, кроме режима экономии трафика». `engine/player.ts` decides when; this module sends
 * the request.
 *
 * ## What the request buys
 *
 * Two things, and the second is the one to count on. The browser may keep the answer and hand
 * it to the element when the track starts — the sound route marks it cacheable for a week
 * (`routes/stream.ts`). And the server, asked for the file, starts filling its cache from
 * Telegram, so the element's own first request is served from disk instead of waiting on the
 * service account. Whether a phone's webview reuses a cached `206` for the element is **not
 * checked**; the server half does not depend on it.
 *
 * ## Why the URL must be the element's own
 *
 * A cached answer is found by its URL. The player hands over the next track's `stream.url`
 * exactly as the element will be given it — never a copy with anything appended.
 *
 * ## One range, and the rest of the header rules
 *
 * `Range: bytes=0-524287` — a single range with a first byte is a CORS-safelisted header, so
 * no preflight; the end is inclusive, so this is exactly 512 KB, one aligned piece of the
 * server's reader. The body is read to the end, because a cache entry is only written for an
 * answer that was read in full, and then dropped.
 *
 * ## Data saver
 *
 * `navigator.connection.saveData` on means no request at all. An absent `connection` — every
 * iOS webview — means «go ahead»: not knowing is not a reason to refuse. The old project also
 * skipped 2G; the specification names only the data saver, so only the data saver is honoured.
 *
 * Nothing here throws or rejects: a failed prefetch is a track that starts a little later, and
 * nobody is told.
 *
 * ## Only for a cut (task 45)
 *
 * When one track goes into the next by an overlap, the other deck itself is given the next
 * track twenty seconds ahead (`engine/player.ts`, «Two decks»), and its own download is the
 * head request. The 512 KB are asked for only where the next track starts at the end of this
 * one: `cut`, or an overlap that cannot be.
 */

/** Bytes of the head. One aligned piece of the server's reader (`media/align.ts`). */
export const PREFETCH_HEAD_BYTES = 524_288;

/** How long before the end of the current track the next one is asked for. */
export const PREFETCH_LEAD_S = 20;

/** The one field of `NetworkInformation` read here. */
interface NetworkInformationLike {
  readonly saveData?: boolean;
}

/** `navigator.connection`, read defensively: absent on iOS, and a getter may throw. */
function connection(): NetworkInformationLike | undefined {
  try {
    const candidate: unknown = (navigator as { connection?: unknown }).connection;
    return typeof candidate === 'object' && candidate !== null ? candidate : undefined;
  } catch {
    return undefined;
  }
}

/** `false` only when the listener has asked the phone to save data. */
export function mayPrefetch(): boolean {
  return connection()?.saveData !== true;
}

/** Asks for the first {@link PREFETCH_HEAD_BYTES} of `url`, unless the data saver is on. */
export function prefetchHead(url: string, fetcher: typeof fetch = fetch): void {
  if (url === '' || !mayPrefetch()) return;
  void (async () => {
    try {
      const response = await fetcher(url, {
        headers: { Range: `bytes=0-${String(PREFETCH_HEAD_BYTES - 1)}` },
        // The sound URL carries its own pass; no cookie is involved.
        credentials: 'omit',
      });
      await response.arrayBuffer();
    } catch {
      // See the header: a prefetch that failed is only a slower start.
    }
  })();
}
